import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, ShieldCheck, Star, Users2, Store, Award, ExternalLink, Loader2, Mail } from "lucide-react";
import AdminMessageUserDialog from "@/components/AdminMessageUserDialog";

// Admin gate: trust the backend `is_admin` flag (single source of truth is
// the ADMIN_EMAIL env server-side). Kept as a UX guard — the API is the
// authoritative security boundary.

function relDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  } catch { return "—"; }
}

function Tab({ active, count, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors"
      style={{
        borderColor: active ? "var(--terracotta)" : "rgba(0,0,0,0.12)",
        backgroundColor: active ? "rgba(31,93,52,0.12)" : "#fff",
        color: active ? "var(--terracotta)" : "var(--ink)",
        fontWeight: active ? 600 : 400,
      }}
    >
      <Icon size={14} />
      {label}
      <span
        className="text-[10px] font-mono px-1.5 py-0.5 rounded"
        style={{
          backgroundColor: active ? "rgba(31,93,52,0.2)" : "rgba(0,0,0,0.06)",
          color: active ? "var(--terracotta)" : "var(--ink-soft)",
        }}
      >
        {count}
      </span>
    </button>
  );
}

function PlanBadge({ plan }) {
  const isPro = plan === "pro";
  return (
    <span
      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{
        backgroundColor: isPro ? "rgba(196,139,46,0.15)" : "rgba(0,0,0,0.06)",
        color: isPro ? "#c48b2e" : "var(--ink-soft)",
      }}
    >
      {isPro ? "Pro" : "Free"}
    </span>
  );
}

export default function AdminUsers() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("vendor");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ vendor: 0, organizer: 0 });
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  // Which user (if any) is currently being messaged via the admin dialog.
  const [messageTarget, setMessageTarget] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const [current, vendors, organizers] = await Promise.all([
        api.get(`/admin/users?role=${role}${debounced ? `&search=${encodeURIComponent(debounced)}` : ""}`),
        // Only re-fetch totals if we don't have them yet OR search is empty (keeps counts sane)
        api.get("/admin/users?role=vendor&limit=1"),
        api.get("/admin/users?role=organizer&limit=1"),
      ]);
      setRows(current.data.users || []);
      setTotals({
        vendor: vendors.data.total || 0,
        organizer: organizers.data.total || 0,
      });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, [role, debounced]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (user && user.is_admin) load();
  }, [user, load]);

  const verifyOrganizer = async (u) => {
    const willVerify = !u.is_verified;
    if (!window.confirm(`${willVerify ? "Verify" : "Un-verify"} ${u.name || u.email}? Verified organizers get a green badge on every event and skip moderation.`)) return;
    try {
      await api.post(`/admin/organizers/${u.id}/verify`, { verified: willVerify });
      toast.success(`${u.name || u.email} → ${willVerify ? "verified" : "un-verified"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const visible = useMemo(() => rows, [rows]);
  const showBusinessCol = role === "vendor" || role === "organizer";
  const showActivity = role === "vendor" ? "applications" : "events";

  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14" data-testid="admin-users-page">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Admin · Directory</div>
      <h1 className="font-serif text-5xl mt-3">Vendors &amp; organizers.</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
        Everyone who&apos;s ever signed up on AddVendTour. Search by name, email, business,
        or @handle. Click any row to view their public profile.
      </p>

      <div className="mt-8 flex gap-3 flex-wrap">
        <Tab
          testId="tab-vendors"
          active={role === "vendor"}
          count={totals.vendor}
          onClick={() => setRole("vendor")}
          icon={Store}
          label="Vendors"
        />
        <Tab
          testId="tab-organizers"
          active={role === "organizer"}
          count={totals.organizer}
          onClick={() => setRole("organizer")}
          icon={Users2}
          label="Organizers"
        />
      </div>

      <div className="mt-6 relative max-w-md">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "var(--ink-soft)" }}
        />
        <Input
          data-testid="users-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${role}s by name, email, business…`}
          className="pl-9"
        />
      </div>

      {err && (
        <div
          data-testid="users-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(31,93,52,0.08)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      <div className="mt-6 rounded-lg border overflow-hidden" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
        <table className="w-full text-sm" data-testid={`users-table-${role}`}>
          <thead style={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium" style={{ color: "var(--ink-soft)" }}>User</th>
              {showBusinessCol && <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium hidden md:table-cell" style={{ color: "var(--ink-soft)" }}>Business</th>}
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium" style={{ color: "var(--ink-soft)" }}>Plan</th>
              {role === "organizer" && (
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium" style={{ color: "var(--ink-soft)" }}>Verified</th>
              )}
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium text-right" style={{ color: "var(--ink-soft)" }}>
                {showActivity === "events" ? "Events" : "Apps"}
              </th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium text-right" style={{ color: "var(--ink-soft)" }}>Ratings</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-medium hidden lg:table-cell" style={{ color: "var(--ink-soft)" }}>Joined</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {busy ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center" style={{ color: "var(--ink-soft)" }}>
                <Loader2 size={16} className="animate-spin inline mr-2" />
                Loading…
              </td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center" style={{ color: "var(--ink-soft)" }}>
                No {role}s match your search.
              </td></tr>
            ) : (
              visible.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  onClick={(e) => {
                    // Ignore clicks that started on an interactive element inside the row
                    // (buttons/links have their own handlers).
                    if (e.target.closest("button, a")) return;
                    navigate(`/admin/users/${u.id}`);
                  }}
                  className="border-t cursor-pointer hover:bg-black/[0.02] transition-colors"
                  style={{ borderColor: "rgba(0,0,0,0.06)" }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.avatar ? (
                        <img
                          src={u.avatar}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover shrink-0"
                          style={{ backgroundColor: "var(--sand)" }}
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-serif"
                          style={{ backgroundColor: "var(--sand)", color: "var(--terracotta)" }}
                        >
                          {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{u.name || <em style={{ color: "var(--ink-soft)" }}>no name</em>}</div>
                        <div className="text-xs truncate" style={{ color: "var(--ink-soft)" }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  {showBusinessCol && (
                    <td className="px-4 py-3 hidden md:table-cell text-xs" style={{ color: "var(--ink-soft)" }}>
                      {u.business_name || "—"}
                    </td>
                  )}
                  <td className="px-4 py-3"><PlanBadge plan={u.plan} /></td>
                  {role === "organizer" && (
                    <td className="px-4 py-3">
                      {u.is_verified ? (
                        <span
                          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ backgroundColor: "rgba(111,125,85,0.15)", color: "var(--moss)" }}
                        >
                          <ShieldCheck size={10} /> Yes
                        </span>
                      ) : (
                        <span className="text-[11px]" style={{ color: "var(--ink-soft)" }}>—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {showActivity === "events" ? u.events_count : u.applications_count}
                  </td>
                  <td className="px-4 py-3 text-right text-xs">
                    {u.ratings_count > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <Star size={11} style={{ color: "#c48b2e" }} />
                        {u.avg_rating.toFixed(1)}
                        <span style={{ color: "var(--ink-soft)" }}>({u.ratings_count})</span>
                      </span>
                    ) : (
                      <span style={{ color: "var(--ink-soft)" }}>—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs" style={{ color: "var(--ink-soft)" }}>
                    {relDate(u.created_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`user-message-${u.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMessageTarget(u);
                        }}
                        className="rounded-full text-xs inline-flex items-center gap-1"
                        style={{
                          borderColor: "var(--terracotta)",
                          color: "var(--terracotta)",
                        }}
                        title={`Send an in-app message to ${u.name || u.email}`}
                      >
                        <Mail size={12} />
                        Message
                      </Button>
                      {u.handle && u.role === "vendor" && (
                        <Link
                          to={`/@${u.handle}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`user-view-${u.id}`}
                          className="p-1.5 rounded-md hover:bg-black/5"
                          title="View public profile"
                        >
                          <ExternalLink size={14} />
                        </Link>
                      )}
                      {u.role === "organizer" && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`user-verify-${u.id}`}
                          onClick={() => verifyOrganizer(u)}
                          className="rounded-full text-xs inline-flex items-center gap-1"
                          style={{
                            borderColor: u.is_verified ? "rgba(0,0,0,0.15)" : "rgba(111,125,85,0.35)",
                            color: u.is_verified ? "var(--ink-soft)" : "var(--moss)",
                          }}
                        >
                          <Award size={12} />
                          {u.is_verified ? "Un-verify" : "Verify"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-right" style={{ color: "var(--ink-soft)" }}>
        Showing {visible.length} of {totals[role] || 0} {role}s
        {debounced ? ` matching "${debounced}"` : ""}.
      </div>

      <AdminMessageUserDialog
        user={messageTarget}
        open={!!messageTarget}
        onOpenChange={(next) => { if (!next) setMessageTarget(null); }}
      />
    </div>
  );
}
